import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../common/Button";
import { parseSchedulingError } from "../../api/Scheduling/errors";

export function blockOverlapFromError(error) {
  const parsed = parseSchedulingError(error);
  if (parsed.code !== "block_overlap_confirmation_required" || !parsed.raw?.confirmationToken || !parsed.raw?.overlaps) return null;
  return { ...parsed.raw.overlaps, confirmationToken: parsed.raw.confirmationToken };
}

export default function BlockOverlapWarning({ overlap, onConfirm, onCancel, busy = false, isEdit = false, hoursSaved = false }) {
  const confirmButton = useRef(null);
  useEffect(() => { confirmButton.current?.focus(); }, [overlap]);
  const { appointmentCount = 0, blockCount = 0 } = overlap;
  const parts = [];
  if (appointmentCount) parts.push(`${appointmentCount} existing appointment${appointmentCount === 1 ? "" : "s"}`);
  if (blockCount) parts.push(`${blockCount} existing block${blockCount === 1 ? "" : "s"}`);
  return <section className="scheduling-overlap-warning" role="alert" aria-label="Overlapping appointments or blocks">
    {hoursSaved && <p>Working hours have been saved. This break needs confirmation.</p>}
    <p>{parts.join(" and ")} overlap this period.</p>
    <p>If proceed to create block, existing appointments remain unchanged and must be rescheduled or cancelled separately. Selected period will be unavailable for new appointment booking.</p>
    <div className="btn-wrap">
      <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      <Button ref={confirmButton} type="button" isLoading={busy} onClick={onConfirm}>{isEdit ? "Save changes" : "Create Block"}</Button>
    </div>
  </section>;
}

/** Break creation shares the block API and must also obtain explicit acknowledgement. */
export function useBreakOverlapConfirmation() {
  const [overlap, setOverlap] = useState(null);
  const resolver = useRef(null);
  useEffect(() => () => { resolver.current?.(false); resolver.current = null; }, []);
  const confirmOverlap = useCallback(next => new Promise(resolve => {
    resolver.current = resolve;
    setOverlap(next);
  }), []);
  const finish = confirmed => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOverlap(null);
  };
  return { confirmOverlap, warning: overlap && <BlockOverlapWarning overlap={overlap} hoursSaved
    onConfirm={() => finish(true)} onCancel={() => finish(false)} /> };
}
